import { requestViaProxy } from '../../utils/proxy';

/**
 * Genera la URL para obtener información GetFeatureInfo de una capa WMS.
 * @param {Object} layer - Capa de OpenLayers.
 * @param {Object} params - Parámetros de la consulta WMS.
 * @param {Object} config - Configuración global (incluye proxy opcional).
 * @returns {string|null} URL de la consulta o null si no se puede construir.
 */
export const getFeatureInfoUrl = (layer, params, config) => {
    const source = layer.getSource();
    const urls = source?.getUrls();
    if (!urls || urls.length === 0) {
        console.error("No se encontraron URLs para el servicio WMS.");
        return null;
    }

    const baseUrl = urls[0];
    const urlParams = new URLSearchParams(params).toString();
    const fullUrl = `${baseUrl}?${urlParams}`;

    return config?.useProxy ? requestViaProxy(fullUrl) : fullUrl;
};

/**
 * Construye los parámetros necesarios para una petición GetFeatureInfo WMS.
 * @param {Object} view - Vista actual del mapa.
 * @param {Array} size - Tamaño del mapa [ancho, alto].
 * @param {Array} pixel - Coordenada del clic en píxeles.
 * @param {Array} extent - Extensión visible del mapa.
 * @param {Array} queryLayers - Capas a consultar.
 * @returns {Object} Parámetros completos para la consulta.
 */
export const buildFeatureInfoParams = (view, size, pixel, extent, queryLayers) => ({
    SERVICE: 'WMS',
    VERSION: '1.3.0',
    REQUEST: 'GetFeatureInfo',
    query_layers: queryLayers.join(','),
    layers: queryLayers.join(','),
    TILED: 'true',
    INFO_FORMAT: 'application/json',
    FORMAT: 'image/png',
    TRANSPARENT: 'true',
    I: Math.round(pixel[0]),
    J: Math.round(pixel[1]),
    WIDTH: size[0],
    HEIGHT: size[1],
    CRS: view.getProjection().getCode(),
    BBOX: extent.join(','),
    buffer: 500,
    feature_count: 50,
});

/**
 * Agrupa las features obtenidas de un WMS por categoría (`cat`).
 * Si no hay categoría, usa el nombre de la capa como fallback.
 * Devuelve un array de objetos con estructura { categoría: [features] }.
 * @param {Array} datos - Features en formato GeoJSON.
 * @param {Array} layers - Capas OpenLayers que corresponden a las features.
 * @returns {Array<Object>} Features agrupadas por categoría.
 */
function agruparFeaturesPorCat(datos, layers = []) {
    const layerFallback = layers.length > 0
        ? (layers[0].get('category') || layers[0].get('title') || layers[0].get('name') || 'Other')
        : 'Other';

    const agrupadas = datos.reduce((acc, feature) => {
        // Identificar la capa OL correspondiente a este feature.
        // GeoServer devuelve feature.id con formato "nombreCapa.123" o "workspace:nombreCapa.123"
        let matchedLayer = null;
        if (layers.length === 1) {
            matchedLayer = layers[0];
        } else if (feature.id) {
            const featureLayerName = feature.id.split('.')[0].split(':').pop();
            matchedLayer = layers.find(l => l.get('name') === featureLayerName) || null;
        }

        const props = { ...feature.properties };
        if (matchedLayer) {
            props._olLayer = matchedLayer;
        }

        const categoria = props.cat
            || (matchedLayer ? (matchedLayer.get('category') || matchedLayer.get('title') || matchedLayer.get('name')) : null)
            || layerFallback;
        if (!acc[categoria]) acc[categoria] = [];
        acc[categoria].push(props);
        return acc;
    }, {});

    return Object.entries(agrupadas).map(([categoria, features]) => ({
        [categoria]: features,
    }));
}

/**
 * Maneja el clic en el mapa para obtener información de capas WMS visibles.
 * @param {Object} evt - Evento de clic en el mapa.
 * @param {Object} map - Instancia de OpenLayers.
 * @param {Object} visibleOverlayLayers - Capas visibles con posibilidad de consulta.
 * @param {Function} setFeatures - Callback para almacenar los resultados agrupados.
 * @param {Object} config - Configuración global del visor.
 */
export const handleMapClickWMS = async (evt, map, visibleOverlayLayers, setFeatures, config) => {
    if (!map) return;

    const view = map.getView();
    const size = map.getSize();
    const pixel = map.getEventPixel(evt.originalEvent);
    const extent = view.calculateExtent(size);

    // Agrupar capas WMS visibles por URL base
    const layerGroups = Object.entries(visibleOverlayLayers).reduce((groups, [layerName, isVisible]) => {
        if (!isVisible) return groups;

        const layer = map
            .getLayers()
            .getArray()
            .find(l => l.get('name') === layerName && l.get('queryable'));

        if (!layer) return groups;

        // comprobar si la capa es de tipo WMS
        const source = layer.getSource();
        if (!source || typeof source.getUrls !== 'function') return groups; // <-- comprobación clave
        const url = source.getUrls()[0];
        if (!url) return groups;

        if (!groups[url]) groups[url] = [];
        groups[url].push(layer);

        return groups;
    }, {});

    // Realizar una petición por cada grupo de capas WMS que comparten URL
    for (const [url, layers] of Object.entries(layerGroups)) {
        const queryLayers = layers.map(layer => layer.get('name'));
        const params = buildFeatureInfoParams(view, size, pixel, extent, queryLayers);
        const featureUrl = getFeatureInfoUrl(layers[0], params, config);

        if (!featureUrl) continue;

        try {
            const response = await fetch(featureUrl);
            const data = await response.json();

            const agrupadas = agruparFeaturesPorCat(data.features || [], layers);
            setFeatures(prev => [...prev, ...agrupadas]);
        } catch (error) {
            console.error('Error obteniendo los features del WMS:', error);
        }
    }
};
